import React from 'react';
import { Box, Text, HStack, Badge, Flex } from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { Target } from 'lucide-react';

const MotionBox = motion.create ? motion.create(Box) : motion(Box);

const DataPreview = ({ columns, rows }) => (
  <Box
    position="relative"
    bg="rgba(255, 255, 255, 0.02)"
    borderBottom="1px solid rgba(255, 255, 255, 0.04)"
    px={3}
    pt={3}
    pb={2}
  >
    <Box
      overflow="hidden"
      sx={{
        maskImage: 'linear-gradient(to right, black 78%, transparent 98%)',
        WebkitMaskImage: 'linear-gradient(to right, black 78%, transparent 98%)',
      }}
    >
      <Flex gap={0} mb={1.5}>
        {columns.map((col, i) => (
          <Text
            key={i}
            fontSize="10px"
            fontWeight="600"
            color="gray.500"
            textTransform="uppercase"
            letterSpacing="0.05em"
            flex="0 0 28%"
            minW="0"
            px={1.5}
          >
            {col}
          </Text>
        ))}
      </Flex>

      {rows.map((row, rowIdx) => (
        <Flex
          key={rowIdx}
          gap={0}
          py={1}
          borderRadius="4px"
          bg={rowIdx % 2 === 1 ? 'rgba(255, 255, 255, 0.015)' : 'transparent'}
        >
          {row.map((cell, cellIdx) => (
            <Text
              key={cellIdx}
              fontSize="11px"
              color="gray.400"
              flex="0 0 28%"
              minW="0"
              isTruncated
              px={1.5}
            >
              {cell}
            </Text>
          ))}
        </Flex>
      ))}
    </Box>
  </Box>
);

const TemplateCard = ({ template, onClick }) => {
  const {
    title,
    description,
    category,
    gradient,
    icon: Icon,
    comingSoon,
    beta,
    previewColumns,
    previewRows,
  } = template;

  return (
    <Box position="relative">
      <MotionBox
        role="group"
        borderRadius="16px"
        overflow="hidden"
        bg="rgba(255, 255, 255, 0.03)"
        border="1px solid"
        borderColor="rgba(255, 255, 255, 0.06)"
        cursor={comingSoon ? 'default' : 'pointer'}
        opacity={comingSoon ? 0.35 : 1}
        whileHover={comingSoon ? undefined : { y: -4 }}
        transition={{ type: 'spring', stiffness: 400, damping: 25 }}
        onClick={() => !comingSoon && onClick?.(template)}
        _hover={
          comingSoon
            ? undefined
            : {
                borderColor: 'rgba(255, 255, 255, 0.14)',
                bg: 'rgba(255, 255, 255, 0.045)',
              }
        }
      >
        {/* Gradient accent line at top — borderTopRadius matches card so it stays inside curves */}
        <Box
          position="absolute"
          top={0}
          left="9px"
          right="9px"
          h="3px"
          bgGradient={`linear(to-r, ${gradient[0]}, ${gradient[1]})`}
          borderBottomRadius="20px"
          borderTopRadius="25px"
          zIndex={2}
        />

        {/* Gradient glow — ambient color wash, borderTopRadius keeps it inside curves */}
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          h="150px"
          bg={`linear-gradient(180deg, ${gradient[0]}28 0%, ${gradient[0]}18 20%, ${gradient[1]}0c 55%, transparent 100%)`}
          borderTopRadius="16px"
          pointerEvents="none"
          _groupHover={comingSoon ? undefined : { bg: `linear-gradient(180deg, ${gradient[0]}38 0%, ${gradient[0]}22 20%, ${gradient[1]}14 55%, transparent 100%)` }}
          transition="background 0.3s ease"
          zIndex={0}
        />

        {/* Card content */}
        <Box position="relative" zIndex={1}>
          <DataPreview columns={previewColumns} rows={previewRows} />

          <Box px={4} pb={4} pt={3}>
            {/* Icon badge */}
            <Box
              display="inline-flex"
              p={2}
              borderRadius="10px"
              bg={`linear-gradient(135deg, ${gradient[0]}20, ${gradient[1]}20)`}
              border="1px solid"
              borderColor={`${gradient[0]}18`}
              mb={2.5}
            >
              <Icon size={16} color={gradient[0]} strokeWidth={2} />
            </Box>

            <HStack spacing={2} alignItems="center" mb={1} flexWrap="wrap">
              <Text fontWeight="600" fontSize="sm" color="white" lineHeight="1.3">
                {title}
              </Text>
              {beta && (
                <Badge
                  fontSize="9px"
                  fontWeight="600"
                  letterSpacing="0.04em"
                  px={1.5}
                  py={0.5}
                  colorScheme="purple"
                  variant="subtle"
                  borderRadius="4px"
                >
                  Beta
                </Badge>
              )}
            </HStack>

            <Text fontSize="xs" color="gray.500" noOfLines={2} mb={3} lineHeight="1.5">
              {description}
            </Text>

            <HStack spacing={1.5}>
              <Target size={11} color="rgba(255,255,255,0.3)" strokeWidth={2} aria-hidden />
              <Text
                as="span"
                fontSize="10px"
                color="gray.600"
                fontWeight="500"
                letterSpacing="0.03em"
              >
                {category}
              </Text>
            </HStack>
          </Box>
        </Box>
      </MotionBox>

      {comingSoon && (
        <Box position="absolute" top={3} right={3} zIndex={3}>
          <Text
            color="#34d399"
            fontSize="11px"
            fontWeight="600"
          >
            Coming Soon
          </Text>
        </Box>
      )}
    </Box>
  );
};

export default TemplateCard;
